import * as Dfns from 'date-fns/fp';
import { useMemo, ReactNode } from 'react';

export const SCENARIO_PRESET_KEYS = {
  SAME_TIME_LAST_YEAR: 'same-time-last-year',
  MOST_RECENT_DATA: 'most-recent-data'
} as const;

export type ScenarioPresetKey = (typeof SCENARIO_PRESET_KEYS)[keyof typeof SCENARIO_PRESET_KEYS];

export interface ScenarioPlanningPreset {
  key: ScenarioPresetKey;
  label: string;
  sublabel: ReactNode;
  start: string;
  end: string;
  disabled: boolean;
}

interface UseScenarioPlanningPresetsArgs {
  start: string;
  end: string;
  planLength: number;
  maxEnd?: string;
  minStart?: string;
}

const ISO_FORMAT = 'yyyy-MM-dd';
const LABEL_FORMAT = 'M/d/yy';

const formatIso = Dfns.format(ISO_FORMAT);
const formatLabel = Dfns.format(LABEL_FORMAT);

const formatLabelRange = (startIso: string, endIso: string): string =>
  `${formatLabel(Dfns.parseISO(startIso))}-${formatLabel(Dfns.parseISO(endIso))}`;

const isBefore = (candidate: string, boundary?: string): boolean =>
  Boolean(boundary) && Dfns.parseISO(candidate) < Dfns.parseISO(boundary as string);

const isAfter = (candidate: string, boundary?: string): boolean =>
  Boolean(boundary) && Dfns.parseISO(candidate) > Dfns.parseISO(boundary as string);

// Scenario planning only accepts Monday start/end; mirror the toolbar's snap.
const snapMondayBack = (date: Date): Date => Dfns.startOfISOWeek(date);
const snapMondayForward = (date: Date): Date =>
  Dfns.isMonday(date) ? date : Dfns.addWeeks(1, Dfns.startOfISOWeek(date));

export const useScenarioPlanningPresets = ({
  start,
  end,
  planLength,
  maxEnd,
  minStart
}: UseScenarioPlanningPresetsArgs): { presets: ScenarioPlanningPreset[] } => {
  const presets = useMemo<ScenarioPlanningPreset[]>(() => {
    // Ends are the Sunday closing the last included week (endOfISOWeek); starts stay Mondays.
    const sameTimeStart = formatIso(snapMondayBack(Dfns.subYears(1, Dfns.parseISO(start))));
    const sameTimeEnd = formatIso(
      Dfns.endOfISOWeek(snapMondayBack(Dfns.subYears(1, Dfns.parseISO(end))))
    );

    const mostRecentEndMonday = snapMondayBack(Dfns.parseISO(maxEnd ?? end));
    const mostRecentEnd = formatIso(Dfns.endOfISOWeek(mostRecentEndMonday));
    // Inclusive count: planLength weeks means planLength-1 weeks back from the end week.
    const mostRecentStart = formatIso(Dfns.subWeeks(planLength - 1, mostRecentEndMonday));

    // Compare against the picker's effective bounds (start→Monday, end→Sunday) so a preset
    // boundary sharing a week with a mid-week minStart/maxEnd isn't wrongly disabled.
    const effectiveMinStart = minStart
      ? formatIso(snapMondayForward(Dfns.parseISO(minStart)))
      : undefined;
    const effectiveMaxEnd = maxEnd
      ? formatIso(Dfns.endOfISOWeek(snapMondayBack(Dfns.parseISO(maxEnd))))
      : undefined;

    const sameTimeDisabled =
      isBefore(sameTimeStart, effectiveMinStart) || isAfter(sameTimeEnd, effectiveMaxEnd);
    const mostRecentDisabled = !maxEnd || isBefore(mostRecentStart, effectiveMinStart);

    return [
      {
        key: SCENARIO_PRESET_KEYS.SAME_TIME_LAST_YEAR,
        label: `Same Time Last Year (${formatLabelRange(sameTimeStart, sameTimeEnd)})`,
        sublabel: (
          <>
            Use when <em>seasonality</em> is most important.
          </>
        ),
        start: sameTimeStart,
        end: sameTimeEnd,
        disabled: sameTimeDisabled
      },
      {
        key: SCENARIO_PRESET_KEYS.MOST_RECENT_DATA,
        label: `Most Recent Data (${formatLabelRange(mostRecentStart, mostRecentEnd)})`,
        sublabel: (
          <>
            Use when <em>recency</em> is most important.
          </>
        ),
        start: mostRecentStart,
        end: mostRecentEnd,
        disabled: mostRecentDisabled
      }
    ];
  }, [start, end, planLength, maxEnd, minStart]);

  return { presets };
};

export const matchPresetKey = (
  presets: ScenarioPlanningPreset[],
  start: string,
  end: string
): ScenarioPresetKey | undefined => presets.find(p => p.start === start && p.end === end)?.key;
